import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import ReportsFilterBar from './ReportsFilterBar';
import { regionDisplayNameById } from './StateToggleList';
import { getStatScopeOption } from '../constants/statScopes';
import { getReportWindowOption } from '../constants/reportWindows';
import { computeScopeProgress, filterVisitedByScope } from '../lib/progressStats';
import { useJurisdictionsCatalog } from '../lib/jurisdictionsCatalog';

const API_BASE_URL =
  process.env.NODE_ENV === 'production' ? 'https://api.platechase.com' : 'http://localhost:8787';

function Reports() {
  const location = useLocation();
  const { denominators, scopeOptions, loaded: catalogLoaded } = useJurisdictionsCatalog();
  const activeScope = (() => {
    const params = new URLSearchParams(location.search);
    const scope = params.get('scope') || 'US';
    return ['US', 'CAN', 'MX', 'COMBINED'].includes(scope) ? scope : 'US';
  })();
  const activeWindow = (() => {
    const params = new URLSearchParams(location.search);
    const windowParam = params.get('window') || 'monthly';
    return ['monthly', 'annual', 'decennial'].includes(windowParam) ? windowParam : 'monthly';
  })();
  const scopeOption = getStatScopeOption(activeScope, catalogLoaded ? scopeOptions : null);
  const windowOption = getReportWindowOption(activeWindow);
  const [adminStatus, setAdminStatus] = useState(null);
  const [gameData, setGameData] = useState([]);
  const [monthYear, setMonthYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const sessionId = Cookies.get('session');

  useEffect(() => {
    if (!sessionId) {
      setError('No session found. Please log in.');
      setLoading(false);
      return;
    }

    fetch(`${API_BASE_URL}/reports?window=${encodeURIComponent(activeWindow)}`, {
      method: 'GET',
      headers: {
        'Authorization': sessionId
      }
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else if (response.status === 403) {
        throw new Error('Access denied. Please log in to view reports.');
      } else {
        throw new Error('Failed to load reports.');
      }
    })
    .then(data => {
      setAdminStatus(data.message);
      setGameData(data.gameData || []);
      setMonthYear(data.monthYear || '');
      setLoading(false);
    })
    .catch(error => {
      setError(error.message);
      setLoading(false);
    });
  }, [sessionId, activeWindow]);

  const getStateName = (stateId) => regionDisplayNameById(stateId);

  const getProgressColor = (progress) => {
    const num = parseFloat(progress);
    if (num >= 80) return '#4CAF50'; // Green
    if (num >= 60) return '#FF9800'; // Orange
    if (num >= 40) return '#FFC107'; // Yellow
    return '#F44336'; // Red
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <div className="privacy-policy">
        <h1>Admin Reports</h1>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.2rem', color: '#4285f4' }}>Loading reports...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="privacy-policy">
        <h1>Reports</h1>
        <section>
          <h2>Access Denied</h2>
          <p>{error}</p>
          <p>Please log in to view reports.</p>
        </section>
      </div>
    );
  }

  // Filter out banned users for statistics and main list
  const nonBannedUsers = gameData.filter(user => !user.bannedAt || typeof user.bannedAt !== 'number');
  const bannedUsers = gameData.filter(user => user.bannedAt && typeof user.bannedAt === 'number');

  const usersWithScopeStats = nonBannedUsers.map((user) => {
    const visitedStates = Array.isArray(user.visitedStates)
      ? user.visitedStates
      : (user.gameData?.visitedStates || []);
    const scopeStats = computeScopeProgress(visitedStates, activeScope, denominators);
    return { ...user, scopeStats, visitedStates };
  });

  const sortedUsers = [...usersWithScopeStats].sort((a, b) => {
    const pctDiff = b.scopeStats.percentNumber - a.scopeStats.percentNumber;
    if (pctDiff !== 0) return pctDiff;
    return b.scopeStats.count - a.scopeStats.count;
  });
  
  const totalUsers = nonBannedUsers.length;
  const totalRegions = usersWithScopeStats.reduce((sum, user) => sum + user.scopeStats.count, 0);
  const averageProgress = totalUsers > 0
    ? (
        usersWithScopeStats.reduce((sum, user) => sum + user.scopeStats.percentNumber, 0) / totalUsers
      ).toFixed(2)
    : 0;

  return (
    <div className="privacy-policy">
      <h1>Reports</h1>
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        Last updated: {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
      </p>

      <section>
        <h2>Access Status</h2>
        <p style={{ color: '#4285f4', fontWeight: 'bold', fontSize: '1.1rem' }}>✅ {adminStatus}</p>
        <p>{adminStatus === 'Authenticated as Admin' ? 'You have full administrative access to the system.' : 'You have user access to anonymized reports.'}</p>
        {adminStatus === 'Authenticated as Admin' && (
          <p>
            <Link to="/admin/jurisdictions">Manage jurisdictions &amp; scopes</Link>
          </p>
        )}
      </section>

      <section>
        <h2>{windowOption.overviewTitle} — {monthYear}</h2>
        <ReportsFilterBar activeScope={activeScope} activeWindow={activeWindow} />
        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem', fontStyle: 'italic' }}>
          {windowOption.description}. Statistics exclude banned users.
          {activeWindow === 'decennial' && ' Decennial loads more history and may take a few seconds.'}
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{totalUsers}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Active Users</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '0.25rem' }}>
              {bannedUsers.length > 0 && `+${bannedUsers.length} banned`}
            </div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{totalRegions}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>{scopeOption.regionsLabel} spotted</div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{averageProgress}%</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Avg {scopeOption.name} Progress</div>
          </div>
        </div>
      </section>

      <section>
        <h2>Active User Progress Details</h2>
        <p>Total active users: <strong>{totalUsers}</strong> | Banned users: <strong style={{ color: '#f44336' }}>{bannedUsers.length}</strong></p>
        
        {totalUsers > 0 ? (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
              gap: '1.5rem'
            }}>
              {sortedUsers.map((user) => {
                const { visitedStates, scopeStats } = user;
                const progress = scopeStats.percent;
                const progressColor = getProgressColor(progress);
                const displayStates =
                  activeScope === 'COMBINED'
                    ? visitedStates
                    : filterVisitedByScope(visitedStates, activeScope);
                
                return (
                  <div key={user.email} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                            {user.email}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#888' }}>
                            {scopeStats.count} / {scopeStats.denominator} spotted
                          </div>
                        </div>
                        {adminStatus === 'Authenticated as Admin' && (
                          <button
                            onClick={() => openModal(user)}
                            style={{
                              background: 'rgba(66, 133, 244, 0.1)',
                              color: '#4285f4',
                              border: '1px solid rgba(66, 133, 244, 0.3)',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(66, 133, 244, 0.2)';
                              e.target.style.borderColor = 'rgba(66, 133, 244, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(66, 133, 244, 0.1)';
                              e.target.style.borderColor = 'rgba(66, 133, 244, 0.3)';
                            }}
                          >
                            More Actions
                          </button>
                        )}
                      </div>
                      <div style={{
                        background: progressColor,
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}>
                        {progress}%
                      </div>
                    </div>
                    
                    {displayStates.length > 0 && (
                      <div>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#888', 
                          marginBottom: '0.5rem',
                          fontWeight: '500'
                        }}>
                          Regions Collected:
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          {displayStates.map((stateId) => (
                            <span key={stateId} style={{
                              background: 'rgba(66, 133, 244, 0.1)',
                              color: '#4285f4',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '15px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              border: '1px solid rgba(66, 133, 244, 0.2)'
                            }}>
                              {getStateName(stateId)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: '#666',
            fontSize: '1.1rem'
          }}>
            No users found in the current month's data.
          </div>
        )}
      </section>

      <section>
        <h2>System Information</h2>
        <p>This reports panel provides real-time access to user data and system analytics for {monthYear}.</p>
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.02)', 
          padding: '1rem', 
          borderRadius: '8px',
          marginTop: '1rem',
          fontSize: '0.9rem',
          color: '#888'
        }}>
          <strong>Data Source:</strong> Cloudflare D1<br/>
          <strong>Update Frequency:</strong> Real-time<br/>
          <strong>Access Level:</strong> {adminStatus === 'Authenticated as Admin' ? 'Administrator (Full Access)' : 'User (Anonymized Data)'}
        </div>
      </section>

      {/* More Actions Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, color: '#fff' }}>
                More Actions - {selectedUser?.email}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#fff'}
                onMouseLeave={(e) => e.target.style.color = '#888'}
              >
                ×
              </button>
            </div>
            
            <div style={{ color: '#ccc', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '1.5rem' }}>
                Administrative actions for user: <strong>{selectedUser?.email}</strong>
              </p>
              
              {/* Show current ban status */}
              {selectedUser?.bannedAt && (
                <div style={{
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#f44336', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    🚫 User is currently banned
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#888' }}>
                    Banned on: {new Date(selectedUser.bannedAt * 1000).toLocaleDateString()}
                  </div>
                </div>
              )}
              
              {/* Ban/Unban button */}
              {selectedUser?.bannedAt ? (
                <button
                  onClick={async () => {
                    const confirmUnban = window.confirm(
                      `Are you sure you want to unban ${selectedUser?.email}?\n\n` +
                      `This action will:\n` +
                      `• Remove the ban from the user\n` +
                      `• Allow them to access the system again\n` +
                      `• Preserve their existing game data\n\n`
                    );
                    
                    if (confirmUnban) {
                      try {
                        const response = await fetch(`${API_BASE_URL}/users/unban`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': sessionId
                          },
                          body: JSON.stringify({ email: selectedUser.email })
                        });
                        
                        if (response.ok) {
                          await response.json();
                          alert(`User ${selectedUser.email} has been unbanned successfully.`);
                          closeModal();
                          // Refresh the page to show updated data
                          window.location.reload();
                        } else {
                          const errorData = await response.json();
                          alert(`Failed to unban user: ${errorData.error || 'Unknown error'}`);
                        }
                      } catch (error) {
                        console.error('Error unbanning user:', error);
                        alert(`Error unbanning user: ${error.message}`);
                      }
                    }
                  }}
                  style={{
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#45a049';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#4CAF50';
                  }}
                >
                  ✅ Unban User
                </button>
              ) : (
                <button
                  onClick={async () => {
                    const confirmBan = window.confirm(
                      `Are you sure you want to ban ${selectedUser?.email}?\n\n` +
                      `This action will:\n` +
                      `• Mark the user as banned\n` +
                      `• Prevent future access to the system\n`
                    );
                    
                    if (confirmBan) {
                      try {
                        const response = await fetch(`${API_BASE_URL}/users/ban`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': sessionId
                          },
                          body: JSON.stringify({ email: selectedUser.email })
                        });
                        
                        if (response.ok) {
                          await response.json();
                          alert(`User ${selectedUser.email} has been banned successfully.`);
                          closeModal();
                          // Refresh the page to show updated data
                          window.location.reload();
                        } else {
                          const errorData = await response.json();
                          alert(`Failed to ban user: ${errorData.error || 'Unknown error'}`);
                        }
                      } catch (error) {
                        console.error('Error banning user:', error);
                        alert(`Error banning user: ${error.message}`);
                      }
                    }
                  }}
                  style={{
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#d32f2f';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f44336';
                  }}
                >
                  🚫 Ban User
                </button>
              )}
              
              <p style={{ 
                fontSize: '0.8rem', 
                color: '#888', 
                marginTop: '1rem',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                This action will permanently remove the user and all associated data.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banned Users Section */}
      {(() => {
        if (bannedUsers.length > 0 && adminStatus === 'Authenticated as Admin') {
          return (
            <section>
              <h2>Banned Users</h2>
              <p style={{ color: '#f44336', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                ⚠️ The following users have been banned from the system
              </p>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
                gap: '1.5rem'
              }}>
                {bannedUsers.map((user) => {
                  const bannedDate = new Date(user.bannedAt * 1000).toLocaleDateString();
                  
                  return (
                    <div key={user.email} style={{
                      background: 'rgba(244, 67, 54, 0.1)',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      border: '2px solid rgba(244, 67, 54, 0.3)',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      position: 'relative'
                    }}>
                      {/* Banned Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '1rem',
                        background: '#f44336',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '15px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        Banned
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <div style={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem', 
                            marginBottom: '0.25rem',
                            color: '#f44336'
                          }}>
                            {user.email}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#888' }}>
                            Banned on: {bannedDate}
                          </div>
                        </div>
                      </div>
                      

                      
                      {/* More Actions Button for Banned Users */}
                      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={() => openModal(user)}
                          style={{
                            background: 'rgba(76, 175, 80, 0.1)',
                            color: '#4CAF50',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(76, 175, 80, 0.2)';
                            e.target.style.borderColor = 'rgba(76, 175, 80, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(76, 175, 80, 0.1)';
                            e.target.style.borderColor = 'rgba(76, 175, 80, 0.3)';
                          }}
                        >
                          More Actions
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        }
        return null;
      })()}
    </div>
  );
}

export default Reports; 